/**
 * @author Brendan Magee, Christine Movius
 * @desc component for notification trigger selection and configuration
 * @example <notification-trigger notification="notification"
 *                                form-submitted="submitted"
 *                                selected="triggerBlockSelected(name, selected)">
 *          </notification-trigger>
 */

import template from './notificationTrigger.html';

function NotificationTriggerController(
  $stateParams,
  $translate,
  notificationService,
  peopleService,
  siteService
) {
  let triggerType;
  let triggerCopy;

  this.$onInit = () => {
    this.newNotification = !$stateParams.notificationId;
    this.initialized = false;
    this.websiteSections = [];

    this.assigneeTypes = [
      {
        id: 'user',
        name: $translate.instant('USER')
      },
      {
        id: 'group',
        name: $translate.instant('GROUP')
      }
    ];

    this.fields = {};
    const available = [
      'priority',
      'pitchStatus',
      'pitch',
      'creators',
      'sections',
      'deadline',
      'taskType',
      'createdBy',
      'contentType',
      'story',
      'assignee',
      'publicationId',
      'publicationName',
      'sectionId',
      'primaryWebsites'
    ];

    available.forEach((field) => {
      this.fields[field] = { visible: false, enabled: false, required: false };
    });

    this.disableSelection = false;
    this.creatorsList = [];
    this.section = null;
    this.website = null;
    this.storyWebsite = null;
    this.assignee = {
      user: null,
      group: null
    };
    this.story = {
      creatorsList: [],
      section: null,
      pitch: null,
      primaryWebsites: []
    };
    this.currentSection = {
      path: null,
      inheritance: true
    };
    this.currentWebsite = {
      id: null,
      name: null
    };
    this.publication = {
      sections: []
    };

    notificationService.initialized.then(() => {
      const schemata = [
        'priorities',
        'sections',
        'platforms',
        'websites',
        'groups',
        'contentTypes',
        'pitchStatuses',
        'publicationPitchStatuses',
        'publications',
        'taskTypes',
        'triggerTypes'
      ];

      schemata.forEach((schema) => {
        if (notificationService[schema] && notificationService[schema].length > 0) {
          this[schema] = notificationService[schema];
        }
      });

      this.typeaheadConfigs = {
        editable: false,
        selectOnExact: true,
        selectOnBlur: true,
        focusOnSelect: false
      };

      this.generateFields(this.notification.trigger.type);
      triggerType = this.notification.trigger.type;
    });
  };

  this.$doCheck = () => {
    if (this.notification.trigger.type !== triggerType) {
      this.generateFields(this.notification.trigger.type);
      triggerType = this.notification.trigger.type;

      // Populates website value when a user updates a "task create" notification
      if (triggerType === 'task_create') {
        const existingTaskCreate = this.notification.trigger;
        this.storyWebsite = existingTaskCreate && existingTaskCreate.story.pitch ?
          existingTaskCreate.story.pitch.website : null;
      }
    }

    if (this.notification.original && this.notification.original.id && !this.initialized) {
      triggerCopy = this.notification.original.trigger;
      this.generateFields(this.notification.trigger.type);

      if (triggerCopy.creators && triggerCopy.creators.length) {
        this.creatorsList = triggerCopy.creators.map(author => ({ username: author }));
      }

      if (triggerCopy.story && triggerCopy.story.creators && triggerCopy.story.creators.length) {
        this.story.creatorsList = triggerCopy.story.creators.map(author => ({ username: author }));
      }

      if (triggerCopy.createdBy) {
        this.taskCreatedBy = triggerCopy.createdBy;
      }

      if (triggerCopy.publicationId) {
        if (this.publications.find(pub => triggerCopy.publicationId === pub.id)) {
          this.publication = this.publications.find(pub => triggerCopy.publicationId === pub.id);
        }
        if (triggerCopy.sectionId) {
          this.publicationSection = this.publication.sections.find(sec =>
            (triggerCopy.sectionId === sec.id));
        }
      }

      if (triggerCopy.assignee && triggerCopy.assignee.type === 'group') {
        this.notification.trigger.assignee.type = 'group';
        this.assignee.group = triggerCopy.assignee.id;
      } else if (triggerCopy.assignee && triggerCopy.assignee.type === 'user') {
        this.notification.trigger.assignee.type = 'user';
        this.assignee.user = triggerCopy.assignee.id;
      }

      this.initialized = true;
    }

    Object.keys(this.fields).forEach((field) => {
      let selected = this.fields[field].enabled;
      if (field === 'sectionId' && this.fields.publicationId.enabled) {
        selected = !!this.notification.trigger.sectionId;
      }
      this.selected({ selected, name: field });
    });

    if (this.formSubmitted) {
      if (this.fields.creators.enabled) {
        this.parent.$setValidity('creators', !!this.notification.trigger.creators.length);
      } else {
        this.parent.$setValidity('creators', true);
      }

      if (this.fields.sections.enabled) {
        this.parent.sections.$setValidity('sections', !!this.notification.trigger.sections.length);
      } else {
        this.parent.sections.$setValidity('sections', true);
      }

      if (this.fields.story.enabled) {
        const { story } = this.notification.trigger;
        const value = !!story.sections.length
          || !!story.creators.length
          || !!story.pitch
          || !!story.primaryWebsites.length;

        this.parent.storySections.$setValidity('storySections', value);
      } else {
        this.parent.storySections.$setValidity('storySections', true);
      }

      if (this.fields.publicationId.enabled) {
        this.parent.publication.$setValidity('publicationId', !!this.notification.trigger.publicationId);
      } else {
        this.parent.publication.$setValidity('publicationId', true);
      }

      if (this.fields.primaryWebsites.enabled) {
        this.parent.primaryWebsites.$setValidity('primaryWebsites', !!this.notification.trigger.primaryWebsites.length);
      } else {
        this.parent.primaryWebsites.$setValidity('primaryWebsites', true);
      }
    }

    this.pubsTitle = this.notification.trigger.type === 'edition_finalize' ? 'PUBLICATION' : 'PITCH';
  };

  this.generateFields = (type) => {
    if (
      this.notification &&
      this.notification.trigger &&
      this.notification.trigger.type &&
      this.triggerTypes &&
      this.triggerTypes.length
    ) {
      const currentType = this.triggerTypes.find(current => current.id === type);

      Object.keys(this.fields).forEach((field) => {
        this.fields[field] = { visible: false, enabled: false, required: false };

        if (currentType.blocks && currentType.blocks.indexOf(field) > -1) {
          this.fields[field].visible = true;
        }

        if (triggerCopy) {
          Object.keys(triggerCopy).forEach((key) => {
            if (key !== 'type' && key !== 'closedPlans') {
              const isArray = Array.isArray(triggerCopy[key]);
              this.fields[key].enabled = isArray ? !!triggerCopy[key].length : true;
            }
          });
        }

        if (currentType.required && currentType.required.indexOf(field) > -1) {
          const defaultValue = currentType.defaults && currentType.defaults.indexOf(field) > -1;
          this.fields[field].enabled = defaultValue;
          this.fields[field].required = true;
        }
      });
    }
  };

  this.togglePitchStatus = (status, type = 'platform') => {
    let pitchStatusArray = this.notification.trigger.pitch.pitchStatus;

    if (type !== 'platform') {
      pitchStatusArray = this.notification.trigger.pitchStatus;
    }

    const activeStatus = pitchStatusArray.findIndex(pitchStatus => pitchStatus === status.id);

    if (activeStatus > -1) {
      pitchStatusArray.splice(activeStatus, 1);
    } else {
      pitchStatusArray.push(status.id);
    }
  };

  this.selectPublicationTrigger = () => {
    if (this.publication && this.publication.id) {
      this.notification.trigger.publicationId = this.publication.id;

      if (this.publicationSection && this.publicationSection.id) {
        this.notification.trigger.sectionId = this.publicationSection.id;
      } else if (!this.publicationSection || this.publicationSection.id) {
        this.notification.trigger.sectionId = null;
      }
    }
  };

  this.setTriggerCreators = (type = 'story') => {
    if (type !== 'story') {
      this.notification.trigger.creators = this.creatorsList.map(creator => creator.username);
    } else {
      const creators = this.story.creatorsList;
      this.notification.trigger.story.creators = creators.map(creator => creator.username);
    }
  };

  this.getUsers = name => peopleService.getUsersByName(name).then(data => data);

  this.selectItem = ($item, type = 'assignee') => {
    if (type === 'assignee') {
      this.notification.trigger.assignee.id = $item.username ? $item.username : $item.id;
    } else if (type === 'section') {
      this.currentSection.path = $item.path;
    } else if (type === 'website') {
      this.currentWebsite = $item;
    } else {
      this.notification.trigger.createdBy = $item.username;
    }
  };

  this.addSection = (type = 'story') => {
    const { trigger } = this.notification;
    let existingSections = [];

    if (trigger.story && trigger.story.sections && trigger.story.sections.length > 0) {
      existingSections = this.notification.trigger.story.sections;
    } else if (trigger.sections && trigger.sections.length > 0) {
      existingSections = this.notification.trigger.sections;
    }

    const current = existingSections.find(sec => sec.path === this.currentSection.path);
    const newSection = {
      path: this.currentSection.path,
      inheritance: this.currentSection.inheritance
    };

    if (type === 'story' && !current) {
      this.notification.trigger.story.sections.push(newSection);
    } else if (type === 'sections' && !current) {
      this.notification.trigger.sections.push(newSection);
    }

    this.currentSection = {
      path: null,
      inheritance: true
    };

    if (type === 'story') {
      this.story.section = null;
    } else {
      this.section = null;
    }
  };

  this.removeSection = ($index, type = 'story') => {
    if (type !== 'story') {
      this.notification.trigger.sections.splice($index, 1);
    } else {
      this.notification.trigger.story.sections.splice($index, 1);
    }
  };

  this.renderSectionName = (section) => {
    const { path } = section;

    return `"${path || $translate.instant('MISSING_PLAN_PATH_ERROR')}"`;
  };

  this.getSections = (id) => {
    this.section = null;
    siteService.getFilteredSections(id)
      .then((sections) => {
        this.websiteSections = sections;
      });
  };

  this.addWebsite = (storyTrigger = false) => {
    const { trigger } = this.notification;
    let current = null;

    if (storyTrigger) {
      current = trigger.story.primaryWebsites.find(site => this.currentWebsite.id === site);
    } else {
      current = trigger.primaryWebsites.find(site => this.currentWebsite.id === site);
    }

    if (!storyTrigger && !current) {
      this.notification.trigger.primaryWebsites.push(this.currentWebsite.id);
    } else if (!current) {
      this.notification.trigger.story.primaryWebsites.push(this.currentWebsite.id);
    }

    this.currentWebsite = {
      id: null,
      name: null
    };

    this.website = null;
  };

  this.removeWebsite = ($index, storyTrigger = false) => {
    if (!storyTrigger) {
      this.notification.trigger.primaryWebsites.splice($index, 1);
    } else {
      this.notification.trigger.story.primaryWebsites.splice($index, 1);
    }
  };

  this.assignWebsite = () => {
    this.notification.trigger.story.pitch.website = this.storyWebsite;
  };
}

const notificationTrigger = {
  template,
  bindings: {
    notification: '=',
    formSubmitted: '=',
    selected: '&'
  },
  require: {
    parent: '^form'
  },
  controller: 'NotificationTriggerController'
};

const controllerArray = [
  '$stateParams',
  '$translate',
  'notificationService',
  'peopleService',
  'siteService',
  NotificationTriggerController
];

export default function (ngModule) {
  ngModule.component('notificationTrigger', notificationTrigger);
  ngModule.controller('NotificationTriggerController', controllerArray);
}
